"use client";

import Link from "next/link";
import { useState, type ReactNode } from "react";
import { Knopf } from "./knopf";
import { LucideSymbol, SeitenIcon } from "./seiten-icons";

/**
 * Bausteine der Bereichsseiten (Musterkatalog M-115, Entscheid Betreiber 19.10.2026 „Variante A“, Design-Guides
 * `bereichs-kopf.md`, `seiten-reiter.md`, `seiten-baum.md`, `bereichs-abschnitt.md`, `ansprechpartner-raster.md`). Rein darstellend:
 * der Aufrufer liefert nur, was die Person lesen darf (r110), und entscheidet nichts über Rechte. Nur Theme-Tokens, kein `style`,
 * kein `dark:`, keine Fremdbilder (Titelbild nur mit geprüfter eigener Adresse, sonst keine Bildfläche).
 */

const SEITENBAUM_RAHMEN = "bg-card border border-card-line shadow-2xs rounded-xl p-2";
const WEBPART_RAHMEN = "bg-card border border-card-line shadow-2xs rounded-xl";

export interface BereichsKopfProps {
  titel: string;
  /** „Abteilung“, „Standort“, „Projekt“, „Thema“. */
  art: string;
  beschreibung?: string | null;
  /** Geprüfte eigene Adresse des Titelbilds; ohne sie keine Bildfläche (kein Platzhalter). */
  titelbildUrl?: string | null;
  /** Platz rechts neben dem Titel (z. B. „Seite bearbeiten“). */
  aktionen?: ReactNode;
}

/**
 * Initialen aus dem Titel: der erste Buchstabe bzw. die erste Ziffer der ersten zwei Wörter („[Demo] Einkauf und Logistik“ wird „DE“,
 * „Einkauf“ wird „E“); Satzzeichen wie „[“ zählen nie. Ohne Treffer „B“.
 */
export function bereichInitialen(titel: string): string {
  const ersteZeichen = titel
    .split(/\s+/)
    .map((w) => /[\p{L}\p{N}]/u.exec(w)?.[0] ?? "")
    .filter((z) => z !== "");
  return ersteZeichen.slice(0, 2).join("").toUpperCase() || "B";
}

/** Kopf einer Bereichsseite: optionales Titelbild-Band, Initialen-Kachel, Titel, Art, Beschreibung. */
export function BereichsKopf({ titel, art, beschreibung, titelbildUrl, aktionen }: BereichsKopfProps) {
  const initial = bereichInitialen(titel);
  const mitBand = Boolean(titelbildUrl);
  return (
    <>
      {/* Preline: application-pages/application-pages-sub-profiles-accounts/application-pages-cat-person-and-account-detail-headers/customer-detail-header-with-avatar-company-link-and-actions */}
      {/* Abweichung AW-123 (2): Titelbild-Band ist kein Katalogblock (Betreiber 19.10.2026 am Bild; Variante A 20.10.2026: Kachel überlappt das Band); Initialen-Kachel statt Personenfoto */}
      <header className="space-y-4">
        {titelbildUrl && (
          <div className="h-32 sm:h-44 w-full overflow-hidden rounded-xl border border-layer-line bg-muted" data-bereich-band>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img className="size-full object-cover" src={titelbildUrl} alt="" />
          </div>
        )}
        <div className={mitBand ? "flex flex-wrap items-start justify-between gap-x-5 gap-y-3 px-1" : "flex flex-wrap items-start justify-between gap-x-5 gap-y-3"}>
          <div className={mitBand ? "flex items-start gap-x-4 min-w-0" : "flex items-center gap-x-4 min-w-0"}>
            <span
              className={
                mitBand
                  ? "relative -mt-10 shrink-0 inline-flex justify-center items-center size-16 rounded-xl border border-card-line bg-card shadow-md text-lg font-semibold text-foreground"
                  : "shrink-0 inline-flex justify-center items-center size-12 rounded-full border border-layer-line bg-layer text-base font-semibold text-foreground"
              }
              aria-hidden="true"
              data-bereich-initialen
            >
              {initial}
            </span>
            <div className={mitBand ? "min-w-0 pt-1" : "min-w-0"}>
              <h1 className="text-lg md:text-xl font-semibold text-foreground break-words">{titel}</h1>
              <p className="text-sm text-muted-foreground-1">{art}</p>
            </div>
          </div>
          {aktionen && <div className="flex flex-wrap items-center gap-2">{aktionen}</div>}
        </div>
        {beschreibung && <p className="max-w-3xl text-sm text-muted-foreground-1 whitespace-pre-line">{beschreibung}</p>}
      </header>
    </>
  );
}

export interface SeitenReiterEintrag {
  id: string;
  label: string;
  href: string;
}

const REITER_GEWAEHLT =
  "px-2.5 py-1.5 mb-2 relative inline-flex items-center gap-x-2 hover:bg-surface-hover text-sm rounded-lg focus:outline-hidden focus:bg-surface-focus after:absolute after:-bottom-2 after:inset-x-0 after:z-1 after:h-0.5 after:pointer-events-none text-foreground after:bg-secondary";
const REITER_SONST =
  "px-2.5 py-1.5 mb-2 relative inline-flex items-center gap-x-2 hover:bg-surface-hover text-sm rounded-lg focus:outline-hidden focus:bg-surface-focus after:absolute after:-bottom-2 after:inset-x-0 after:z-1 after:h-0.5 after:pointer-events-none text-muted-foreground-1";

/** Reiter einer Bereichsseite als Links auf Unterrouten (kein Client-Zustand); der gewählte trägt `aria-current="page"`. */
export function SeitenReiter({ eintraege, aktiv, ariaLabel = "Bereich" }: { eintraege: readonly SeitenReiterEintrag[]; aktiv: string; ariaLabel?: string }) {
  return (
    <>
      {/* Preline: navigation-and-layout/navigation-and-layout-sub-headers-navigation/navigation-and-layout-cat-secondary-navs/bottom-bordered-secondary-nav */}
      {/* Abweichung AW-123 (3): Links mit aria-current statt tablist (Seitenwechsel, kein Panel-Wechsel); Querscroll-Wrapper auf schmalen Breiten */}
      <div className="overflow-x-auto">
        <nav aria-label={ariaLabel} className="relative flex gap-x-1 min-w-max after:absolute after:bottom-0 after:inset-x-0 after:border-b after:border-line-2">
          {eintraege.map((e) => (
            <Link key={e.id} href={e.href} aria-current={e.id === aktiv ? "page" : undefined} className={e.id === aktiv ? REITER_GEWAEHLT : REITER_SONST}>
              {e.label}
            </Link>
          ))}
        </nav>
      </div>
    </>
  );
}

export interface SeitenBaumKnoten {
  id: string;
  titel: string;
  href: string;
  /** 0 = Übersicht (Wurzel), 1, 2. */
  tiefe: number;
  aktiv: boolean;
  /** Lucide-Name aus der festen Liste (`SEITEN_ICONS`); fehlt oder unbekannt = Standard-Icon. */
  icon?: string | null;
  /** Anzahl der direkten Unterseiten (Pille rechts); 0 oder fehlend = keine Pille. Zählt Seiten, nie Nutzung (r177). */
  zaehler?: number;
  kinder: SeitenBaumKnoten[];
}

const BAUM_LINK_BASIS = "min-w-0 grow flex items-center gap-x-2 py-1.5 px-2 text-sm rounded-lg focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";
// Aktiver Punkt farbig (Variante A, Betreiber 20.10.2026): getönter Grund in der Primärfarbe, Pille in voller Primärfarbe.
const BAUM_LINK_AKTIV = `${BAUM_LINK_BASIS} bg-primary-100 text-primary font-medium`;
const BAUM_LINK_SONST = `${BAUM_LINK_BASIS} hover:bg-muted-hover text-muted-foreground-1`;
const ZAEHLER_AKTIV = "shrink-0 text-xs py-0.5 px-1.5 rounded-full bg-primary text-primary-foreground";
const ZAEHLER_SONST = "shrink-0 text-xs py-0.5 px-1.5 rounded-full bg-muted text-muted-foreground-1";

const traegtAktive = (k: SeitenBaumKnoten): boolean => k.aktiv || k.kinder.some(traegtAktive);

function BaumZeile({ k }: { k: SeitenBaumKnoten }) {
  const [offen, setOffen] = useState(() => traegtAktive(k));
  const link = (
    <Link href={k.href} aria-current={k.aktiv ? "page" : undefined} className={k.aktiv ? BAUM_LINK_AKTIV : BAUM_LINK_SONST} data-baum-icon={k.icon ?? "FileText"}>
      <SeitenIcon name={k.icon} />
      <span className="grow min-w-0 truncate">{k.titel}</span>
      {k.zaehler !== undefined && k.zaehler > 0 && (
        <span className={k.aktiv ? ZAEHLER_AKTIV : ZAEHLER_SONST} data-baum-zaehler={k.zaehler}>
          <span className="sr-only">{k.zaehler === 1 ? "1 Unterseite" : `${k.zaehler} Unterseiten`}</span>
          <span aria-hidden="true">{k.zaehler}</span>
        </span>
      )}
    </Link>
  );
  if (k.kinder.length === 0) {
    return (
      <li>
        <div className="flex items-center ps-6">{link}</div>
      </li>
    );
  }
  return (
    <li>
      <div className="flex items-center gap-x-0.5">
        <Knopf
          nurSymbol
          variante="ghost"
          groesse="xs"
          starr
          ariaExpanded={offen}
          label={`${k.titel}: Unterseiten ${offen ? "zuklappen" : "aufklappen"}`}
          onClick={() => setOffen(!offen)}
          symbol={
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              {offen ? <path d="m6 9 6 6 6-6" /> : <path d="m9 18 6-6-6-6" />}
            </svg>
          }
        />
        {link}
      </div>
      {offen && (
        <ul className="ms-3 mt-0.5 space-y-0.5 border-s border-line-2 ps-1">
          {k.kinder.map((c) => (
            <BaumZeile key={c.id} k={c} />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Seitenbaum eines Bereichs als aufklappbare Navigation (bis zwei Ebenen unter der Übersicht). Aufgeklappt ist anfangs, wer die
 * gewählte Seite trägt (der Pfad zur aktiven Seite); Auf- und Zuklappen ist reiner Anzeigezustand, nichts wird gespeichert (r177).
 */
export function SeitenBaum({ wurzel, ariaLabel = "Seiten des Bereichs" }: { wurzel: SeitenBaumKnoten; ariaLabel?: string }) {
  return (
    <>
      {/* Preline: navigation-and-layout/navigation-and-layout-sub-sidebars-shells/navigation-and-layout-cat-dashboard-sidebars/classic-dashboard-sidebar-layout */}
      {/* Abweichung AW-123 (4): nur der Akkordeon-Teil der Seitenleiste; React-Zustand statt hs-accordion, ohne Logo/Projektwechsler/dark:/style= */}
      {/* Variante A (20.10.2026): Icon je Seite, farbig markierter aktiver Punkt, Zähler der Unterseiten, Überschrift „Seiten im Bereich“ */}
      <nav aria-label={ariaLabel} className={SEITENBAUM_RAHMEN}>
        <p className="px-2 pb-2 pt-1 text-xs font-medium text-muted-foreground-1 uppercase">Seiten im Bereich</p>
        <ul className="space-y-0.5">
          <BaumZeile k={wurzel} />
        </ul>
      </nav>
    </>
  );
}

// 12er-Raster (Betreiber 20.10.2026): Spannen je Spalte als feste Klassen (nie zusammengesetzt). Unter `lg` stehen die Spalten
// untereinander; vier Spalten stehen ab `md` zu zweit.
const SPALTE_SPANNE: Record<number, string> = {
  3: "min-w-0 space-y-5 lg:col-span-3",
  4: "min-w-0 space-y-5 lg:col-span-4",
  6: "min-w-0 space-y-5 lg:col-span-6",
  8: "min-w-0 space-y-5 lg:col-span-8",
  9: "min-w-0 space-y-5 lg:col-span-9",
  12: "min-w-0 space-y-5 lg:col-span-12",
};
const RASTER_12 = "grid grid-cols-1 lg:grid-cols-12 gap-5";
const RASTER_12_VIER = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5";

/**
 * Ein Abschnitt der Seite, jede Spalte ein Stapel der Webparts (Variante A: volle Breite je Abschnitt). `spannen` (seit 20.10.2026):
 * Breiten der Spalten auf dem 12er-Raster (Summe 12, z. B. `[4, 8]`); ohne `spannen` gleich breite Spalten wie bisher.
 */
export function BereichsAbschnitt({ titel, spalten, spannen }: { titel?: string; spalten: ReactNode[]; spannen?: readonly number[] }) {
  const breiten = spannen && spannen.length === spalten.length ? spannen : spalten.map(() => Math.floor(12 / Math.max(1, spalten.length)));
  return (
    <>
      {/* Abweichung AW-123 (5): Raster-Gerüst ist Layout, kein Katalogblock (Entscheidungsvorlage 19.10.2026, K2) */}
      {/* Abweichung AW-126 (2): 12er-Raster mit festen Aufteilungen `lg:grid-cols-12` und `lg:col-span-n` (Betreiber 20.10.2026), unter lg gestapelt */}
      <section className="space-y-3" aria-label={titel}>
        {titel && <h2 className="text-lg font-semibold text-foreground">{titel}</h2>}
        <div className={spalten.length === 4 ? RASTER_12_VIER : RASTER_12} data-raster={breiten.join("-")}>
          {spalten.map((s, i) => (
            <div key={i} className={SPALTE_SPANNE[breiten[i] ?? 12] ?? SPALTE_SPANNE[12]}>
              {s}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

/**
 * Mini-Skizze einer Aufteilung (Bildleiste der Abschnitts-Werkzeuge): je Spalte ein Balken in ihrer Breite auf dem 12er-Raster. Rein
 * dekorativ (`aria-hidden`), die Bedeutung trägt die Beschriftung daneben. Farbe aus `currentColor` (der Aufrufer setzt den Ton).
 */
export function AufteilungsSkizze({ spannen, className = "shrink-0 w-12 h-7" }: { spannen: readonly number[]; className?: string }) {
  const breite = 48;
  const luecke = 2;
  const nutzbar = breite - luecke * (spannen.length - 1);
  let x = 0;
  const balken = spannen.map((s) => {
    const w = (nutzbar * s) / 12;
    const r = { x, w };
    x += w + luecke;
    return r;
  });
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 28" fill="currentColor" aria-hidden="true" data-skizze={spannen.join("-")}>
      {balken.map((b, i) => (
        <rect key={i} x={b.x} y={0} width={b.w} height={28} rx={2} />
      ))}
    </svg>
  );
}

export interface AnsprechpartnerDaten {
  id: string;
  name: string;
  funktion?: string | null;
}

/** Ansprechpartner als Raster aus Initialen-Kreisen, Name und Funktion (keine Fotos: Personenbezug sparsam, kein Fremdbild). */
export function AnsprechpartnerRaster({ personen, ariaLabel = "Ansprechpartner" }: { personen: readonly AnsprechpartnerDaten[]; ariaLabel?: string }) {
  return (
    <>
      {/* Preline: marketing/marketing-sub-trust-social-proof/marketing-cat-team-sections/circular-team-portrait-grid */}
      {/* Abweichung AW-123 (6): Initialen statt Porträtfoto, ohne Marketing-Titel und „We are hiring“-Pille */}
      <ul aria-label={ariaLabel} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-8">
        {personen.map((p) => (
          <li key={p.id} className="text-center" data-ansprechpartner={p.id}>
            <span className="inline-flex justify-center items-center size-20 rounded-full border border-layer-line bg-layer text-xl font-semibold text-foreground" aria-hidden="true">
              {p.name.trim().slice(0, 1).toUpperCase() || "?"}
            </span>
            <div className="mt-2 sm:mt-3">
              <h3 className="font-medium text-foreground">{p.name}</h3>
              {p.funktion && <p className="text-sm text-muted-foreground-2">{p.funktion}</p>}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Rahmen eines Webparts mit Überschrift und optionalem „Alle ansehen“-Link (Fuß), wie die übrigen Karten der Intranet-Flächen. */
export function WebpartKarte({ id, titel, fussHref, fussText = "Alle ansehen", children }: { id: string; titel: string; fussHref?: string; fussText?: string; children: ReactNode }) {
  return (
    <>
      {/* Preline: dashboards/admin-dashboard/index.html#browsers-card */}
      <section aria-labelledby={`${id}-titel`} className={WEBPART_RAHMEN} data-webpart={id}>
        <div className="p-4 md:p-5 space-y-3">
          <h3 id={`${id}-titel`} className="text-base font-semibold text-foreground">
            {titel}
          </h3>
          {children}
        </div>
        {fussHref && (
          <div className="px-4 md:px-5 py-3 border-t border-card-line">
            <Link href={fussHref} className="text-sm font-medium text-primary hover:text-primary-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus rounded-xs">
              {fussText}
            </Link>
          </div>
        )}
      </section>
    </>
  );
}

export interface BildKachelDaten {
  id: string;
  titel: string;
  /** Ziel: interner Pfad (`/…`) oder http(s)-Adresse; der Aufrufer hat es geprüft. */
  href: string;
  /** Geprüfte eigene Bildadresse (`/api/intranet/bild/<id>`). */
  bildUrl: string;
}

const KACHEL_RAHMEN =
  "group h-full flex flex-col bg-card border border-card-line shadow-2xs rounded-xl overflow-hidden hover:shadow-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus transition";

/**
 * Bildkacheln (Webpart „Bildkacheln“, Variante B der Entscheidungsvorlage 20.10.2026): je Kachel Bild, Titel und Ziel; die ganze Kachel
 * ist EIN Link. Das Bild kommt nur mit geprüfter eigener Adresse (kein Fremdbild, kein Platzhalter), dekorativ (`alt=""`), der Titel
 * trägt die Aussage. Externe Ziele (http/https) öffnen in neuem Tab mit `rel="noopener noreferrer"`. Rein darstellend: der Aufrufer
 * liefert nur Kacheln, deren Bild zum Bereich gehört und deren Ziel gültig ist.
 */
export function BildKachelRaster({ kacheln, ariaLabel = "Bildkacheln" }: { kacheln: readonly BildKachelDaten[]; ariaLabel?: string }) {
  return (
    <>
      {/* Preline: blog-and-articles/blog-and-articles-sub-blog-discovery-sections/blog-and-articles-cat-blog-listing-sections/image-post-card-grid-with-category-meta */}
      {/* Abweichung AW-123 (8): Struktur der Blog-Kachel ohne Kategorie, Datum und Autor; die ganze Kachel ist ein Link; Pfeil statt Meta */}
      <ul aria-label={ariaLabel} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {kacheln.map((k) => {
          const extern = !k.href.startsWith("/");
          const inhalt = (
            <>
              <span className="block aspect-video w-full overflow-hidden bg-muted">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img className="size-full object-cover" src={k.bildUrl} alt="" loading="lazy" />
              </span>
              <span className="flex items-center justify-between gap-x-3 p-4">
                <span className="text-base font-semibold text-foreground group-hover:text-primary">{k.titel}</span>
                <LucideSymbol name="ArrowRight" className="shrink-0 size-4 text-primary" />
              </span>
            </>
          );
          return (
            <li key={k.id} data-bildkachel={k.id}>
              {extern ? (
                <a className={KACHEL_RAHMEN} href={k.href} target="_blank" rel="noopener noreferrer">
                  {inhalt}
                  <span className="sr-only">(öffnet in neuem Tab)</span>
                </a>
              ) : (
                <Link className={KACHEL_RAHMEN} href={k.href}>
                  {inhalt}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}
